/**
 * Debug material panel — applies a single material globally to all objects
 * when enabled. Provides controls for every material system feature
 * (albedo, normal, roughness, metallic, emission) with procedural textures
 * and intensity sliders.
 *
 * Opens alongside the debug overlay (when debugEnabled is true).
 */

import { useCallback } from "react";
import { useStore } from "../app/store.ts";
import {
  DEFAULT_DEBUG_MATERIAL,
  type DebugMaterialConfig,
} from "../viewport/debugMaterial.ts";
import { Button } from "./Button.tsx";
import { ColorPicker } from "./ColorPicker.tsx";
import { CopyButton } from "./CopyButton.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import { Slider } from "./Slider.tsx";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_MUTED,
  FONT_SIZE_TINY,
  RADIUS_XS,
  Z_DEBUG,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  background: "rgba(0,0,0,0.85)",
  color: "#0f0",
  fontFamily: "monospace",
  fontSize: FONT_SIZE_TINY,
  padding: "6px 10px",
  borderRadius: "0 0 6px 6px",
  pointerEvents: "auto",
  maxWidth: 280,
  lineHeight: 1.5,
};

const sectionBorder: React.CSSProperties = {
  borderTop: "1px solid #333",
  marginTop: 4,
  paddingTop: 4,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 2,
};

const labelStyle: React.CSSProperties = {
  minWidth: 60,
  color: COLOR_TEXT_DIM,
  fontSize: FONT_SIZE_TINY,
};

const valueStyle: React.CSSProperties = {
  minWidth: 32,
  textAlign: "right",
  color: COLOR_TEXT_MUTED,
  fontSize: FONT_SIZE_TINY,
};

const toggleStyle = (active: boolean): React.CSSProperties => ({
  background: active ? "rgba(90,160,232,0.3)" : COLOR_SURFACE,
  border: "1px solid " + (active ? COLOR_ACCENT : COLOR_BORDER_STRONG),
  color: active ? COLOR_ACCENT : COLOR_TEXT_DIM,
  borderRadius: RADIUS_XS,
  padding: "2px 6px",
  fontSize: FONT_SIZE_TINY,
  cursor: "pointer",
  fontFamily: "monospace",
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugMaterialPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.debugMaterial);
  const debugMaterial = useStore((s) => s.debugMaterial);
  const setDebugMaterial = useStore((s) => s.setDebugMaterial);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-material", () =>
    togglePanel("debugMaterial"),
  );

  const update = useCallback(
    (patch: Partial<DebugMaterialConfig>) => {
      const current = debugMaterial ?? DEFAULT_DEBUG_MATERIAL;
      setDebugMaterial({ ...current, ...patch });
    },
    [debugMaterial, setDebugMaterial],
  );

  const toggle = useCallback(() => {
    if (debugMaterial) setDebugMaterial(null);
    else setDebugMaterial({ ...DEFAULT_DEBUG_MATERIAL });
  }, [debugMaterial, setDebugMaterial]);

  const getPlainText = useCallback(() => {
    const c = debugMaterial ?? DEFAULT_DEBUG_MATERIAL;
    const on = debugMaterial != null;
    const lines: string[] = [
      `MATERIAL TEST: ${on ? "ON" : "OFF"}`,
      `color: ${c.color}`,
      `roughness: ${c.roughness.toFixed(2)}`,
      `metalness: ${c.metalness.toFixed(2)}`,
      `shading: ${c.flatShading ? "flat" : "smooth"}`,
      `albedo map: ${c.albedoMapEnabled ? `on (scale ${c.albedoMapScale})` : "off"}`,
      `normal map: ${c.normalMapEnabled ? `on (intensity ${c.normalMapIntensity.toFixed(2)})` : "off"}`,
      `roughness map: ${c.roughnessMapEnabled ? `on (scale ${c.roughnessMapScale})` : "off"}`,
      `metallic map: ${c.metallicMapEnabled ? "on" : "off"}`,
      `emissive: ${c.emissiveColor} intensity ${c.emissiveIntensity.toFixed(1)}`,
      `emission map: ${c.emissionMapEnabled ? "on" : "off"}`,
      `opacity: ${c.opacity.toFixed(2)}`,
      `specular: ${c.specularIntensity.toFixed(2)}`,
      `clearcoat: ${c.clearcoat.toFixed(2)} roughness ${c.clearcoatRoughness.toFixed(2)}`,
      `sheen: ${c.sheen.toFixed(2)} roughness ${c.sheenRoughness.toFixed(2)} color ${c.sheenColor}`,
      `transmission: ${c.transmission.toFixed(2)} IOR ${c.ior.toFixed(2)} thickness ${c.thickness.toFixed(1)}`,
    ];
    return lines.join("\n");
  }, [debugMaterial]);

  if (!debugEnabled || !panelOpen) return null;

  const cfg = debugMaterial ?? DEFAULT_DEBUG_MATERIAL;
  const active = debugMaterial != null;

  return (
    <DraggablePanel
      id="debug-material"
      defaultPosition={{ top: 8, right: 396 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 300 }}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          <span style={{ color: "#ff0", fontWeight: "bold" }}>
            MATERIAL TEST
          </span>
          <Button
            onClick={toggle}
            style={toggleStyle(active)}
            data-no-drag
            variant="ghost"
            size="xs"
          >
            {active ? "ON" : "OFF"}
          </Button>
          <span style={{ marginLeft: "auto" }}>
            <CopyButton getText={getPlainText} />
          </span>
        </div>
      }
    >
      <div style={panelStyle}>
        {/* Base color */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>color</span>
            <ColorPicker
              value={cfg.color}
              onChange={(hex) => update({ color: hex })}
              disabled={!active}
              size={20}
            />
            <span style={{ color: COLOR_TEXT_MUTED, fontSize: FONT_SIZE_TINY }}>
              {cfg.color}
            </span>
          </div>
        </div>

        {/* Roughness */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>rough</span>
            <Slider
              value={cfg.roughness}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ roughness: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.roughness.toFixed(2)}</span>
          </div>
        </div>

        {/* Metalness */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>metal</span>
            <Slider
              value={cfg.metalness}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ metalness: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.metalness.toFixed(2)}</span>
          </div>
        </div>

        {/* Flat shading */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>shading</span>
            <Button
              onClick={() => update({ flatShading: !cfg.flatShading })}
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.flatShading)}
            >
              {cfg.flatShading ? "flat" : "smooth"}
            </Button>
          </div>
        </div>

        {/* Albedo map */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>albedo</span>
            <Button
              onClick={() =>
                update({ albedoMapEnabled: !cfg.albedoMapEnabled })
              }
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.albedoMapEnabled)}
            >
              checker
            </Button>
          </div>
          {cfg.albedoMapEnabled && (
            <div style={rowStyle}>
              <span style={labelStyle}>scale</span>
              <Slider
                value={cfg.albedoMapScale}
                min={2}
                max={32}
                step={2}
                onChange={(v) => update({ albedoMapScale: v })}
                disabled={!active}
                dimChromeOnDrag
              />
              <span style={valueStyle}>{cfg.albedoMapScale}</span>
            </div>
          )}
        </div>

        {/* Normal map */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>normal</span>
            <Button
              onClick={() =>
                update({ normalMapEnabled: !cfg.normalMapEnabled })
              }
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.normalMapEnabled)}
            >
              bumps
            </Button>
          </div>
          {cfg.normalMapEnabled && (
            <div style={rowStyle}>
              <span style={labelStyle}>intensity</span>
              <Slider
                value={cfg.normalMapIntensity}
                min={0}
                max={3}
                step={0.05}
                onChange={(v) => update({ normalMapIntensity: v })}
                disabled={!active}
                dimChromeOnDrag
              />
              <span style={valueStyle}>
                {cfg.normalMapIntensity.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Roughness map */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>rough map</span>
            <Button
              onClick={() =>
                update({ roughnessMapEnabled: !cfg.roughnessMapEnabled })
              }
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.roughnessMapEnabled)}
            >
              checker
            </Button>
          </div>
          {cfg.roughnessMapEnabled && (
            <div style={rowStyle}>
              <span style={labelStyle}>scale</span>
              <Slider
                value={cfg.roughnessMapScale}
                min={2}
                max={32}
                step={2}
                onChange={(v) => update({ roughnessMapScale: v })}
                disabled={!active}
                dimChromeOnDrag
              />
              <span style={valueStyle}>{cfg.roughnessMapScale}</span>
            </div>
          )}
        </div>

        {/* Metallic map */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>metal map</span>
            <Button
              onClick={() =>
                update({ metallicMapEnabled: !cfg.metallicMapEnabled })
              }
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.metallicMapEnabled)}
            >
              gradient
            </Button>
          </div>
        </div>

        {/* Emission */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>emit clr</span>
            <ColorPicker
              value={cfg.emissiveColor}
              onChange={(hex) => update({ emissiveColor: hex })}
              disabled={!active}
              size={20}
            />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>emit str</span>
            <Slider
              value={cfg.emissiveIntensity}
              min={0}
              max={5}
              step={0.1}
              onChange={(v) => update({ emissiveIntensity: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.emissiveIntensity.toFixed(1)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>emit map</span>
            <Button
              onClick={() =>
                update({ emissionMapEnabled: !cfg.emissionMapEnabled })
              }
              disabled={!active}
              variant="ghost"
              size="xs"
              style={toggleStyle(cfg.emissionMapEnabled)}
            >
              glow
            </Button>
          </div>
        </div>

        {/* Opacity */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>opacity</span>
            <Slider
              value={cfg.opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ opacity: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.opacity.toFixed(2)}</span>
          </div>
        </div>

        {/* Specular */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>specular</span>
            <Slider
              value={cfg.specularIntensity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ specularIntensity: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.specularIntensity.toFixed(2)}</span>
          </div>
        </div>

        {/* Clearcoat */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>coat</span>
            <Slider
              value={cfg.clearcoat}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ clearcoat: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.clearcoat.toFixed(2)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>coat rough</span>
            <Slider
              value={cfg.clearcoatRoughness}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ clearcoatRoughness: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.clearcoatRoughness.toFixed(2)}</span>
          </div>
        </div>

        {/* Sheen */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>sheen</span>
            <Slider
              value={cfg.sheen}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ sheen: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.sheen.toFixed(2)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>sheen rgh</span>
            <Slider
              value={cfg.sheenRoughness}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ sheenRoughness: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.sheenRoughness.toFixed(2)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>sheen clr</span>
            <ColorPicker
              value={cfg.sheenColor}
              onChange={(hex) => update({ sheenColor: hex })}
              disabled={!active}
              size={20}
            />
          </div>
        </div>

        {/* Transmission / Glass */}
        <div style={sectionBorder}>
          <div style={rowStyle}>
            <span style={labelStyle}>transmit</span>
            <Slider
              value={cfg.transmission}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => update({ transmission: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.transmission.toFixed(2)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>IOR</span>
            <Slider
              value={cfg.ior}
              min={1}
              max={2.5}
              step={0.01}
              onChange={(v) => update({ ior: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.ior.toFixed(2)}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>thickness</span>
            <Slider
              value={cfg.thickness}
              min={0}
              max={10}
              step={0.1}
              onChange={(v) => update({ thickness: v })}
              disabled={!active}
              dimChromeOnDrag
            />
            <span style={valueStyle}>{cfg.thickness.toFixed(1)}</span>
          </div>
        </div>
      </div>
    </DraggablePanel>
  );
}
